import { useEffect } from "react";

/**
 * Sets the <title>, meta description, canonical URL and social tags for a page.
 *
 * Cleanup on unmount is not needed because the next page mounts a fresh PageHead
 * that overrides the values. When a value goes from set → null between pages,
 * the tag is removed so we don't leak stale metadata across routes.
 */
export default function PageHead({
    title,
    description,
    canonical,
    ogImage,
    /** "website" (default) or "article" — controls og:type and Twitter card. */
    type = "website",
    /** Site-wide brand name (og:site_name). */
    siteName = "O Homem da Obra",
    /** ISO string — only used when type="article". */
    publishedAt,
    /** Human name — only used when type="article". */
    author,
    /** When true, adds <meta name="robots" content="noindex, nofollow">. */
    noindex = false,
}) {
    useEffect(() => {
        if (title) document.title = title;

        const upsert = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement(attrs.tag || "meta");
                Object.entries(attrs).forEach(([k, v]) => {
                    if (k === "tag" || v == null) return;
                    el.setAttribute(k, v);
                });
                document.head.appendChild(el);
            } else {
                Object.entries(attrs).forEach(([k, v]) => {
                    if (k === "tag" || v == null) return;
                    el.setAttribute(k, v);
                });
            }
            return el;
        };
        const remove = (selector) => {
            const el = document.head.querySelector(selector);
            if (el) el.remove();
        };

        if (description) {
            upsert('meta[name="description"]', { name: "description", content: description });
            upsert('meta[property="og:description"]', { property: "og:description", content: description });
            upsert('meta[name="twitter:description"]', { name: "twitter:description", content: description });
        }
        if (title) {
            upsert('meta[property="og:title"]', { property: "og:title", content: title });
            upsert('meta[name="twitter:title"]', { name: "twitter:title", content: title });
        }
        if (canonical) {
            upsert('link[rel="canonical"]', { tag: "link", rel: "canonical", href: canonical });
            upsert('meta[property="og:url"]', { property: "og:url", content: canonical });
        }
        upsert('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });
        upsert('meta[property="og:type"]', { property: "og:type", content: type });

        if (ogImage) {
            upsert('meta[property="og:image"]', { property: "og:image", content: ogImage });
            upsert('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
            upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
        } else {
            // Ensure a sensible default card even without image
            upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary" });
        }

        // Article-specific tags
        if (type === "article" && publishedAt) {
            upsert('meta[property="article:published_time"]', { property: "article:published_time", content: publishedAt });
        } else {
            remove('meta[property="article:published_time"]');
        }
        if (type === "article" && author) {
            upsert('meta[property="article:author"]', { property: "article:author", content: author });
        } else {
            remove('meta[property="article:author"]');
        }

        // Robots
        if (noindex) {
            upsert('meta[name="robots"]', { name: "robots", content: "noindex, nofollow" });
        } else {
            remove('meta[name="robots"]');
        }
    }, [title, description, canonical, ogImage, type, siteName, publishedAt, author, noindex]);

    return null;
}
